import { urlVariables } from "./constants";
import { screenSizeMatch } from "./types";

export function helperResizeListener(): screenSizeMatch {
  // Set again the true height when device orientation is changed
  document.documentElement.style.setProperty(
    "--inner-height",
    window?.innerHeight?.toString() + "px"
  );

  // Looks if screen remains tight to show options based on screen size
  const screenMatch = {
    mobileSizeScreen: window.matchMedia("only screen and (max-width: 680px)")
      .matches,
    hoverSizeScreen: window.matchMedia("(any-hover: hover)").matches,
  };

  return screenMatch;
}

export function helperSetIframeWidth(iframe: HTMLIFrameElement): void {
  document.documentElement.style.setProperty(
    "--iframe-width",
    getIframeWidth(iframe)?.toString() + "px"
  );
}

/**
 * Get the minimum width between the iframe content and the window width
 * to set the iframe width
 * @param {HTMLIFrameElement} iframe - the iframe element to get the width from
 * @returns {number} - the width of the iframe
 */
function getIframeWidth(iframe: HTMLIFrameElement): number {
  const width = Math.min(
    iframe.contentDocument.body.offsetWidth,
    window.innerWidth
  );
  return width;
}

/**
 * Function to sort alphabetically an array of objects by some specific key.
 *
 * @param {String} property Key of the object to sort.
 * @param {Number} sortOrder Order to sort the objects (-1 descending, 1 ascending)
 */
export function dynamicSort(property, sortOrder = 1) {
  return function (a, b) {
    if (sortOrder == -1) {
      return b[property]?.localeCompare(a[property]);
    } else {
      return a[property]?.localeCompare(b[property]);
    }
  };
}

// Removes the opacity applied to the navigator
export function RemoveNavigatorOpacity() {
  const container = document.getElementsByClassName(
    "container-main-content"
  )[0];
  container?.classList?.remove("container-main-content--hidden");
}

/*
  Function to identify if the parent of the element is the container.
  If positive, it means that the element is in the publication content, and could be selected.
*/
export function checkIfParentIsContainer(element: HTMLElement): boolean {
  if (element.id === "pageContainer") {
    return true;
  }
  if (element.parentElement) {
    return checkIfParentIsContainer(element.parentElement);
  }
  return false;
}

/*
  Get the URL variables to check if debug mode is enabled
*/
export function getDebugMode(): boolean {
  // Sanity check to avoid crashing if window is not defined
  if (typeof window === "undefined") return false;
  const queryString = window?.location?.search;
  if (!queryString) return false;
  const urlParams = new URLSearchParams(queryString);
  if (urlParams?.get(urlVariables.debug) === "true") return true;
  return false;
}

export async function getJsonFromURL(url: string): Promise<any> {
  const response = await fetch(url, {
    credentials: "include",
  });

  if (!response.ok) {
    // TODO: we should probably not fail silently here
    return {};
  }

  return await response.json();
}

import { toRaw, isRef, isReactive, isProxy } from "vue";

// https://github.com/vuejs/core/issues/5303#issuecomment-1543596383
export function deepToRaw<T extends Record<string, any>>(sourceObj: T): T {
  const objectIterator = (input: any): any => {
    if (Array.isArray(input)) {
      return input.map((item) => objectIterator(item));
    }
    if (isRef(input) || isReactive(input) || isProxy(input)) {
      return objectIterator(toRaw(input));
    }
    if (input && typeof input === "object") {
      return Object.keys(input).reduce((acc, key) => {
        acc[key as keyof typeof acc] = objectIterator(input[key]);
        return acc;
      }, {} as T);
    }
    return input;
  };

  return objectIterator(sourceObj);
}

/**
 * Function to check if the accessible tooltips are not outside the current container
 * @param element - The HTMLElement with the accessible tooltip
 * @param padding - The minimun margin between the tooltip and the edge of the screen
 * @param container - The HTMLElement that contains the tooltip - null if the container is the window
 * @returns void
 */
export const adjustTooltipPosition = (
  element: HTMLElement,
  padding = 10,
  container = null
) => {
  const tooltip = element.querySelector(".accessible-tooltip") as HTMLElement;
  let leftBoundary = 0;
  let rightBoundary = 0;

  if (!tooltip) return;
  const elementRect = element.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  if (!container) {
    leftBoundary = 0;
    rightBoundary = window.innerWidth;
  } else {
    leftBoundary = container.getBoundingClientRect().left;
    rightBoundary = container.getBoundingClientRect().right;
  }

  // Axis X adjustment (if the tooltip goes out of the screen on the right side or left side)
  let tooltipLeft = elementRect.left;

  if (tooltipLeft - tooltipRect.width / 2 <= leftBoundary) {
    tooltipLeft = leftBoundary - tooltipLeft + padding;
  } else if (tooltipLeft + tooltipRect.width > rightBoundary) {
    tooltipLeft = rightBoundary - tooltipLeft - tooltipRect.width - padding;
  } else {
    tooltipLeft = elementRect.width / 2 - tooltipRect.width / 2;
  }

  // Axis Y adjustment (if the tooltip goes out of the screen on the top side or bottom side)
  let tooltipTop = elementRect.top - tooltipRect.height - padding;
  if (tooltipTop < 0) {
    tooltipTop = elementRect.bottom;
  } else {
    tooltipTop = tooltipTop - elementRect.top;
  }

  tooltip.style.left = `${tooltipLeft}px`;
  tooltip.style.top = `${tooltipTop}px`;
};
